import { useEffect, useMemo } from 'react';
import { useOrgFilter, pickDefaultOrg } from '@/store/defaults';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { orgs } from '@/api/catalogs';
import type { ColDef } from 'ag-grid-community';
import { refs, type ProductionNonstandardRow } from '@/api/refs';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { accounts } from '@/api/documents';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import AccountSelect from '@/components/AccountSelect';

/**
 * «Рахунки обліку» меню Виробництво (рішення власника 2026-09-01) —
 * виробничий ЗРІЗ єдиної матриці правил ref.account_rules (Норма #25):
 *  - дефолтні рахунки за ВИДАМИ виробничої номенклатури (видові правила,
 *    сід 0416) — редагуються тут же (upsert kind-only правила);
 *  - фіксовані рахунки механізму (23 НЗВ / 26 випуск / 91 розподіл) —
 *    довідково, з підказкою де перебиваються;
 *  - нестандартні рахунки (точкові правила + картки номенклатури
 *    виробничих видів) — read-only дзеркало, як на /refs/account-rules.
 * Жодного нового backend-механізму: та сама матриця, той самий резолвер.
 */
const PERMISSION = 'refs.account_rules.manage';

// Виробничі види (коди сіда) → дефолтний рахунок сіда.
const PROD_KINDS: Array<{ code: string; seedAcc: string }> = [
  { code: 'PRODUCT', seedAcc: '26' },
  { code: 'SEMIPRODUCT', seedAcc: '25' },
  { code: 'RAW', seedAcc: '201' },
  { code: 'MATERIAL', seedAcc: '201' },
  { code: 'MBP', seedAcc: '221' },
];

// Фіксовані рахунки виробничого механізму (довідково).
const MECHANISM_ROWS = [
  {
    name: 'Незавершене виробництво (НЗВ)', code: '23',
    hint: 'Перебивається у документі «Звіт виробництва» — поле «Рах. витрат» рядка продукції.',
  },
  {
    name: 'Випуск готової продукції', code: '26',
    hint: 'Перебивається у «Звіті виробництва» — поле «Рах. обліку» рядка продукції (або видовим правилом нижче).',
  },
  {
    name: 'Загальновиробничі витрати', code: '91',
    hint: 'Розподіляються на 23 автоматично при закритті місяця пропорційно прямим витратам.',
  },
];

export default function ProductionAccountRulesPage() {
  const qc = useQueryClient();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useOrgFilter();
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);
  const canManage = !!orgId && allowedOrgIds.has(orgId);

  const rulesQ = useQuery({
    queryKey: ['account-rules', orgId],
    queryFn: () => refs.accountRules.list(orgId),
    enabled: !!orgId,
  });
  const kindsQ = useQuery({ queryKey: ['nomenclature-kinds'], queryFn: refs.nomenclatureKinds.list });
  const accountsQ = useQuery({
    queryKey: ['accounts', 'all'],
    queryFn: () => accounts.list({ only_postable: true, limit: 600 }),
    staleTime: 60 * 60 * 1000,
  });
  const accById = useMemo(() => {
    const m = new Map<string, { code: string; name: string }>();
    accountsQ.data?.forEach((a) => m.set(a.id, { code: a.code, name: a.name }));
    return m;
  }, [accountsQ.data]);

  // Виробничі види tenant'а (з сід-кодами) у порядку PROD_KINDS.
  const prodKinds = useMemo(() => {
    const byCode = new Map((kindsQ.data ?? []).map((k) => [k.code ?? '', k]));
    return PROD_KINDS
      .map((pk) => ({ ...pk, kind: byCode.get(pk.code) }))
      .filter((pk) => !!pk.kind);
  }, [kindsQ.data]);

  // kind-only правило (вид без номенклатури/складу) — «дефолт за видом».
  const kindRuleByKindId = useMemo(() => {
    const m = new Map<string, NonNullable<typeof rulesQ.data>[number]>();
    for (const r of rulesQ.data ?? []) {
      if (r.nomenclature_kind_id && !r.nomenclature_id && !r.warehouse_id
          && r.is_active && !r.deletion_mark) {
        const prev = m.get(r.nomenclature_kind_id);
        if (!prev || r.priority < prev.priority) m.set(r.nomenclature_kind_id, r);
      }
    }
    return m;
  }, [rulesQ.data]);

  const upsert = useMutation({
    mutationFn: async (p: { kindId: string; accountId: string }) => {
      const existing = kindRuleByKindId.get(p.kindId);
      if (existing) {
        return refs.accountRules.update(existing.id, { account_id: p.accountId || null });
      }
      return refs.accountRules.create({
        organization_id: orgId,
        nomenclature_kind_id: p.kindId,
        account_id: p.accountId,
        priority: 9,
        is_active: true,
        comment: 'Дефолт виду (сторінка «Рахунки обліку» виробництва)',
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['account-rules', orgId] }),
  });

  // ч.647: нестандартні — серверна пагінація (після клону 1С карткових
  // рахунків можуть бути тисячі; зведення RULE+CARD робить backend).
  const { datasource: nsDatasource, total: nsTotal } =
    useInfiniteGridDatasource<ProductionNonstandardRow>(
      (limit, offset) => refs.accountRules.productionNonstandard({
        organization_id: orgId, limit, offset,
      }),
      [orgId],
    );
  const nsCols = useMemo<ColDef<ProductionNonstandardRow>[]>(() => [
    {
      field: 'nomenclature_name', headerName: 'Номенклатура', flex: 1,
      minWidth: 220, sortable: false,
      cellRenderer: (p: { data?: ProductionNonstandardRow }) => (
        <span>
          {p.data?.nomenclature_name ?? ''}
          {p.data?.external_source && (
            <span title="Імпортовано з 1С" style={{
              color: '#2563eb', border: '1px solid var(--badge-blue-bd)', borderRadius: 4,
              fontSize: '0.7rem', fontWeight: 700, padding: '0 4px', marginLeft: 6,
            }}>I</span>
          )}
        </span>
      ),
    },
    { field: 'kind_name', headerName: 'Вид', width: 160, sortable: false },
    {
      field: 'source', headerName: 'Джерело', width: 180, sortable: false,
      valueFormatter: (p) => p.value === 'RULE' ? 'точкове правило' : 'картка номенклатури',
    },
    {
      field: 'account_code', headerName: 'Рахунок', width: 130, sortable: false,
      cellRenderer: (p: { data?: ProductionNonstandardRow }) => (
        <span>
          <b style={p.data?.usable ? undefined
            : { color: '#dc2626', textDecoration: 'line-through' }}>
            {p.data?.account_code ?? '—'}
          </b>
          {p.data && !p.data.usable && (
            <span
              title="Рахунок непридатний для проводки (група / видалений). У реальній проводці спрацює стандартний рахунок."
              style={{ color: '#dc2626', fontSize: '0.78rem', marginLeft: 6 }}>
              ⚠
            </span>
          )}
        </span>
      ),
    },
  ], []);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Рахунки обліку виробництва</h2>
      </div>
      {/* Фільтр орг — рядком під назвою (канон). Без «Усі організації»:
          правила рахунків ведуться per-org. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
        flexWrap: 'wrap', marginBottom: '1rem', marginTop: '-0.5rem' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 260, flex: '0 0 auto' }}>
          <option value="">— юрособа —</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
      </div>

      <h3 style={{ margin: '0.5rem 0 0.3rem', fontSize: '1rem' }}>
        Рахунки за замовчуванням (за видом номенклатури)
      </h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: '0 0 0.4rem' }}>
        Це видові правила єдиної матриці «Рахунки обліку» — зміна тут діє на всі
        документи (надходження, виробництво, реалізація). Точкове правило або
        рахунок на картці товару перебивають дефолт виду.
      </p>
      <table className="data-table" style={{ width: '100%', fontSize: '0.88rem' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Вид номенклатури</th>
            <th style={{ textAlign: 'left', width: 340 }}>Рахунок за замовчуванням</th>
            <th style={{ textAlign: 'left' }}>Типовий</th>
          </tr>
        </thead>
        <tbody>
          {prodKinds.map((pk) => {
            const rule = kindRuleByKindId.get(pk.kind!.id);
            const cur = rule?.account_id ?? '';
            return (
              <tr key={pk.code}>
                <td>{pk.kind!.name}</td>
                <td>
                  {canManage ? (
                    <AccountSelect value={cur}
                      onChange={(accountId) => upsert.mutate({ kindId: pk.kind!.id, accountId })} />
                  ) : (
                    <b>{cur ? accById.get(cur)?.code ?? '—' : '—'}</b>
                  )}
                </td>
                <td style={{ color: 'var(--text-secondary)' }}>{pk.seedAcc}
                  {cur && accById.get(cur)?.code !== pk.seedAcc && (
                    <span style={{ color: 'var(--badge-amber-fg)', marginLeft: 8 }}>змінено</span>
                  )}
                </td>
              </tr>
            );
          })}
          {prodKinds.length === 0 && (
            <tr><td colSpan={3} style={{ color: 'var(--text-secondary)' }}>Види номенклатури не знайдено</td></tr>
          )}
        </tbody>
      </table>

      <h3 style={{ margin: '1.2rem 0 0.3rem', fontSize: '1rem' }}>
        Рахунки виробничого механізму
      </h3>
      <table className="data-table" style={{ width: '100%', fontSize: '0.88rem' }}>
        <tbody>
          {MECHANISM_ROWS.map((r) => (
            <tr key={r.code}>
              <td style={{ width: 280 }}>{r.name}</td>
              <td style={{ width: 60 }}><b>{r.code}</b></td>
              <td style={{ color: 'var(--text-secondary)' }}>{r.hint}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {nsTotal !== 0 && (
        <div style={{ marginTop: '1.2rem' }}>
          <h3 style={{ margin: '0 0 0.3rem', fontSize: '1rem' }}>
            Нестандартні рахунки виробничої номенклатури<TitleTotal total={nsTotal} />
          </h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: '0 0 0.4rem' }}>
            Конкретні товари/матеріали з рахунком, що перебиває дефолт виду.
            Редагуються на <Link to="/refs/account-rules">сторінці правил</Link> або
            на картці номенклатури.
          </p>
          <InfiniteDocGrid<ProductionNonstandardRow>
            datasource={nsDatasource}
            columnDefs={nsCols}
            storageKey="production-nonstandard-accounts"
          />
        </div>
      )}
    </div>
  );
}
